/**
 * Linha de status de envio (barra superior) — FUNÇÕES PURAS, sem React/redux/IO.
 *
 * Fonte dos números: PendingSummary (src/utils/pendingData.ts, E1) — a mesma definição
 * de "pendente" do heartbeat e das travas de limpeza/logout. Regras (plano PG-062):
 *  - vermelho (recusados = DLQ) vence amarelo (pendentes);
 *  - N = registros sujos; se zero, itens da fila. NUNCA soma os dois;
 *  - eventos de observabilidade são telemetria interna: não entram no número nem na cor;
 *  - fonte ilegível sem pendência conhecida => neutro (nunca verde "por omissão").
 */
import type { PendingSummary } from './pendingData'

export type StatusTone = 'neutral' | 'pending' | 'ok' | 'refused'

export type StatusLineInput = {
  now: number
  online: boolean
  summary: PendingSummary | null
  /** Epoch ms do último envio aceito pelo servidor (circuitBreaker 'push'). */
  lastPushOkAt: number | null
}

export type StatusLine = {
  tone: StatusTone
  /** Número do selo; null quando não há número a mostrar. */
  count: number | null
  /** Linha completa (desktop): "● 3 aguardando envio". */
  text: string
  /** Texto do selo (mobile): "3", "99+", "✓", "?". */
  badge: string
  /** Frase completa para tooltip / aria-label. */
  detail: string
}

/** Acima disso o "há X" some do verde (um "há 3 dias" soa como "faz 3 dias que não sincroniza"). */
export const OK_AGO_MAX_MIN = 60

const MIN = 60 * 1000

export function formatAgo(now: number, then: number): string {
  const diff = now - then
  if (!(diff >= MIN)) return 'há menos de 1 min'
  const min = Math.floor(diff / MIN)
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.floor(h / 24)
  return `há ${d} ${d === 1 ? 'dia' : 'dias'}`
}

/** Quantos registros aguardam envio. Sujos = medida real; fila = mecanismo de retry (não somar). */
export function pendingCount(summary: PendingSummary): number {
  return summary.dirtyRecords > 0
    ? summary.dirtyRecords
    : summary.queuePending + summary.queueInProgress
}

/** Fontes ilegíveis que importam ao usuário ('events' é só telemetria). */
export function relevantUnreadable(summary: PendingSummary): string[] {
  return summary.unreadable.filter((s) => s !== 'events')
}

const badgeOf = (n: number) => (n > 99 ? '99+' : String(n))
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

export function deriveSyncStatusLine(input: StatusLineInput): StatusLine {
  const { now, online, summary, lastPushOkAt } = input

  if (!summary) {
    return {
      tone: 'neutral',
      count: null,
      text: 'Não foi possível verificar',
      badge: '?',
      detail: 'Não foi possível verificar o que falta enviar.',
    }
  }

  const pending = pendingCount(summary)
  const refused = summary.deadLetter
  const offlineDetail = online
    ? ''
    : ' Sem internet: o envio retoma quando a conexão voltar.'

  if (refused > 0) {
    const pendingDetail =
      pending > 0
        ? ` ${pending} ${plural(pending, 'registro aguarda', 'registros aguardam')} envio.`
        : ''
    return {
      tone: 'refused',
      count: refused,
      text: `⚠ ${refused} ${plural(refused, 'recusado', 'recusados')}`,
      badge: badgeOf(refused),
      detail:
        `${refused} ${plural(refused, 'registro foi recusado', 'registros foram recusados')} pelo servidor.` +
        pendingDetail +
        offlineDetail,
    }
  }

  if (pending > 0) {
    return {
      tone: 'pending',
      count: pending,
      text: `● ${pending} aguardando envio${online ? '' : ' · sem internet'}`,
      badge: badgeOf(pending),
      detail:
        `${pending} ${plural(pending, 'registro aguarda', 'registros aguardam')} envio.` +
        offlineDetail,
    }
  }

  if (relevantUnreadable(summary).length > 0) {
    return {
      tone: 'neutral',
      count: null,
      text: 'Não foi possível verificar',
      badge: '?',
      detail: 'Não foi possível verificar tudo o que falta enviar.',
    }
  }

  const showAgo =
    lastPushOkAt != null && now - lastPushOkAt <= OK_AGO_MAX_MIN * MIN
  const ago = showAgo ? ` · ${formatAgo(now, lastPushOkAt as number)}` : ''
  return {
    tone: 'ok',
    count: null,
    text: `✓ Tudo enviado${ago}`,
    badge: '✓',
    detail: `Tudo enviado${ago}.`,
  }
}
